import { ORDER } from '../core/systems.js';

/**
 * Impact feedback: hitstop, camera shake, spark routing.
 *
 * `src/combat/combat.js` calls world.impact.hit(...) explicitly, once per body,
 * with a real direction. This module therefore does NOT also listen on the 'hit'
 * bus: doing both fired two sparks per blow — the second with a hard-coded
 * (0,1,0) direction — and stacked hitstop and shake, which on a 24-body sweep
 * turned the screen white and froze the sim. The bus handler survives only as a
 * fallback for damage sources that predate the explicit call, and disarms itself
 * the moment combat proves it is routing impact itself.
 */
/** Reused — a 25-body sweep must not allocate 25 objects a frame (CONTRACTS 6). */
const _sopt = { power: 1, kind: 'slash', priority: 2 };
const _dopt = { power: 1, scale: 1, priority: 0, dirX: 0, dirY: 1, dirZ: 0 };
/**
 * impact kind -> the `hit_*` def `world.vfx.spark()` looks up. `slash` and
 * `blunt` and `pierce` exist verbatim; explicit high-value events map to their
 * authored primary. Ordinary kills are handled separately as death puffs.
 */
const SPARK_KIND = { slash: 'slash', blunt: 'blunt', pierce: 'pierce',
  critical: 'crit', guard: 'block', guardBreak: 'crit', land: 'blunt' };

export function createImpact() {
  let routed = false;          // set once combat routes a hit explicitly
  let busHits = 0;

  return {
    name: 'impact', order: ORDER.COMBAT + 1,
    init(w) {
      w.impact = {
        /** kind: slash | kill | guard | guardBreak | land | blunt | critical */
        hit(kind, x, y, z, dx, dy, dz, power = 1, opts) {
          routed = true;
          const p = Math.min(3, power || 1);
          // Hitstop scales with the blow, but many bodies in one sweep must not
          // multiply it — combat passes an index so later bodies contribute less.
          const idx = opts?.index || 0;
          const falloff = 1 / (1 + idx);
          // A move that declares `hitstop: 'none'` means it — the musou grinder
          // fires eight 360 arcs a second and a per-body freeze on every one of
          // them turns the whole flurry into a stutter.
          if (!opts?.noStop) w.engine.hitstop((kind === 'kill' ? 0.075 : 0.05) * p * falloff);
          // NO camera shake here. `src/combat/combat.js` already shakes ONCE per
          // sweep with an amplitude scaled by the hit count, which is the right
          // shape. This used to shake per BODY as well, and shake() re-arms its
          // timer on every call, so a 24-body C4 restarted the same wobble two
          // dozen times: the impact critic measured ch3 running 0.683 s and 8.5
          // oscillations against a plate that wants 0.12-0.22 s and 2-4. A long
          // wobbly shake is the cheapest-looking thing a game can do. The bus
          // fallback below shakes for itself, since nothing else does it there.
          // This used to be `vfx.slash(...) ?? vfx.spark(...)`, but `slash()`
          // returns the vfx api, which is truthy, so the `??` branch was DEAD:
          // every body in a sweep got a 1.7-3.1 m crescent CARD and the compact
          // contact burst (hot core + radial streaks + point light) never played
          // at all. A 20-body sweep became a curtain of identical crescents.
          // The blade ribbon is already VFX's own job on the clip's `swoosh`
          // marker — once per blade pass, which is where it belongs — so the
          // per-BODY call is the spark and only the spark.
          const explicitCrit = kind === 'critical' || kind === 'guardBreak' ||
            !!(opts?.critical || opts?.crit || opts?.officer);
          if (kind === 'kill' && !explicitCrit) {
            // A grunt KO is punctuation, not a critical. Keep it at the feet
            // and let the body silhouette remain readable through the puff.
            _dopt.power = p; _dopt.scale = 0.75 + 0.18 * Math.min(1, p);
            _dopt.priority = idx < 3 ? 1 : 0;
            _dopt.dirX = dx; _dopt.dirY = dy; _dopt.dirZ = dz;
            const gy = w.terrain?.heightAt?.(x, z) ?? y;
            w.vfx?.emit?.('death_puff', x, gy + 0.08, z, _dopt);
            return;
          }
          const base = explicitCrit ? 'crit' : (SPARK_KIND[kind] || 'slash');
          const fill = idx >= 3 && (base === 'slash' || base === 'blunt' || base === 'pierce');
          _sopt.power = p; _sopt.kind = fill ? base + '_fill' : base;
          _sopt.priority = fill ? 1 : 2;
          w.vfx?.spark?.(x, y, z, dx, dy, dz, p, _sopt);
        },
      };

      // Fallback only: anything still emitting bare 'hit' events without going
      // through world.impact.hit(). Goes quiet as soon as combat is live.
      w.bus.on('hit', (id, src, amt, opts) => {
        if (routed || opts?.quiet) return;
        if (busHits++ > 4096) return;
        const e = w.entities;
        const q = Math.min(2, amt / 60);
        w.impact.hit('slash', e.x[id], e.y[id] + 1.0, e.z[id], 0, 1, 0, q);
        w.camrig?.shake?.(0.22 * q, 24, 0.16);
        routed = false;         // this path is the fallback, not a real routing
      });
    },
  };
}
